/**
 * Zugriff auf Bilder im Dokument.
 *
 * Eingebettete Bilder (`inlinePictures`) gibt es ab WordApi 1.1. Frei
 * positionierte Formen und Textfelder (`shapes`) nur in der Desktop-Version –
 * für sie liefert `readShapes` sonst `null`.
 */

import { supportsWordApiDesktop } from "./capabilities";
import { isWordAvailable, WordUnavailableError } from "./document";

export interface PictureInfo {
  index: number;
  /** Alternativtext für Screenreader. Leer, wenn keiner gesetzt ist. */
  altText: string;
  /** Breite und Höhe in Punkt. */
  width: number | null;
  height: number | null;
}

export async function readPictures(): Promise<PictureInfo[]> {
  if (!isWordAvailable()) throw new WordUnavailableError();

  return Word.run(async (context) => {
    const pictures = context.document.body.inlinePictures;
    pictures.load("items/altTextDescription,items/width,items/height");
    await context.sync();

    return pictures.items.map((picture, index) => ({
      index,
      altText:
        typeof picture.altTextDescription === "string" ? picture.altTextDescription.trim() : "",
      width: typeof picture.width === "number" ? picture.width : null,
      height: typeof picture.height === "number" ? picture.height : null,
    }));
  });
}

/** Bilder ohne Alternativtext – für Screenreader unsichtbar. */
export function picturesWithoutAltText(pictures: PictureInfo[]): PictureInfo[] {
  return pictures.filter((picture) => picture.altText.length === 0);
}

/**
 * Ein brauchbarer Alternativtext beschreibt den Bildinhalt. Ein Dateiname oder
 * ein einzelnes Wort erfüllt diesen Zweck nicht.
 */
export function hasMeaningfulAltText(picture: PictureInfo): boolean {
  const text = picture.altText;
  if (text.length < 10) return false;
  if (/\.(png|jpe?g|gif|bmp|tiff?|webp)$/i.test(text)) return false;
  if (/^(bild|foto|grafik|image|screenshot)\s*\d*$/i.test(text)) return false;
  return text.split(/\s+/).length >= 2;
}

export interface ShapeInfo {
  index: number;
  altText: string;
  type: string;
}

/**
 * Frei positionierte Formen und Textfelder.
 * `null`, wenn das Add-in nicht in Word für Windows oder Mac läuft.
 */
export async function readShapes(): Promise<ShapeInfo[] | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApiDesktop("1.2")) return null;

  try {
    return await Word.run(async (context) => {
      const shapes = context.document.body.shapes;
      shapes.load("items/altTextDescription,items/type");
      await context.sync();

      return shapes.items.map((shape, index) => ({
        index,
        altText:
          typeof shape.altTextDescription === "string" ? shape.altTextDescription.trim() : "",
        type: typeof shape.type === "string" ? shape.type : "Unknown",
      }));
    });
  } catch {
    return null;
  }
}

export interface FormMitUmbruch {
  index: number;
  /** "Picture", "TextBox", "GeometricShape", "Group", "Canvas" … */
  type: string;
  /**
   * Textumbruch: "Square", "Tight", "Through", "TopBottom", "Behind", "Front"
   * – oder "Inline" für Formen, die wie ein Zeichen im Text stehen.
   */
  wrap: string | null;
  /** Text eines Textfelds; leer bei Formen ohne Text. */
  text: string;
}

/**
 * Frei positionierte Formen samt Textumbruch und Textinhalt.
 * `null`, wenn das Add-in nicht in Word für Windows oder Mac läuft.
 */
export async function readFormenMitUmbruch(): Promise<FormMitUmbruch[] | null> {
  if (!isWordAvailable()) throw new WordUnavailableError();
  if (!supportsWordApiDesktop("1.2")) return null;

  try {
    return await Word.run(async (context) => {
      const shapes = context.document.body.shapes;
      shapes.load("items/type,items/textWrap/type");
      await context.sync();

      const texte = shapes.items.map((shape) => {
        if (shape.type !== "TextBox") return null;
        const body = shape.body;
        body.load("text");
        return body;
      });
      await context.sync();

      return shapes.items.map((shape, index) => ({
        index,
        type: typeof shape.type === "string" ? shape.type : "Unknown",
        wrap: typeof shape.textWrap?.type === "string" ? shape.textWrap.type : null,
        text: (texte[index]?.text ?? "").trim(),
      }));
    });
  } catch {
    return null;
  }
}
